import { useEmployeeFeedbackStore } from "@/stores/employee-feedback.store";
import { api } from "@/services/api";
import { useDeviceStore } from "@/stores/device.store";
import { EmployeeFeedbackActiveAtAction, EmployeeFeedbackAnswerOptionDto } from "./api/services";

/**
 * This class manages all employee feedback related functionalities.
 * It is a singleton class that ensures only one instance of the class is created.
 *
 * @singleton
 */
export class EmployeeFeedbackService {
    private static instance: EmployeeFeedbackService;
    private timeout: number | undefined;
    private static intervalMilliseconds = 10 * 60 * 1000;

    private constructor() {
        if (EmployeeFeedbackService.instance) {
            throw new Error("Use EmployeeFeedbackService.getInstance() instead of new");
        }
    }

    /**
     * Returns the singleton instance of EmployeeFeedbackService.
     * @returns {EmployeeFeedbackService} The singleton instance.
     */
    public static getInstance(): EmployeeFeedbackService {
        if (!EmployeeFeedbackService.instance) {
            EmployeeFeedbackService.instance = new EmployeeFeedbackService();
        }
        return EmployeeFeedbackService.instance;
    }

    /**
     * Sets up the repeating question fetching.
     * This method should be called when the application starts.
     */
    public static setup(): void {
        const employeeFeedbackService = EmployeeFeedbackService.getInstance();
        employeeFeedbackService.setupRepeatingQuestionFetching();
        employeeFeedbackService.fetchQuestions();
    }

    /**
     * Schedules the next fetch of questions.
     */
    public async setupRepeatingQuestionFetching(): Promise<void> {
        // clear timeout
        if (this.timeout != undefined) clearTimeout(this.timeout);
        // schedule next ping
        this.timeout = setTimeout(async () => {
            await this.repeatingFetchQuestions();
        }, EmployeeFeedbackService.intervalMilliseconds);
    }

    /**
     * Runs the fetchQuestions method and schedules the next fetch.
     */
    private async repeatingFetchQuestions(): Promise<void> {
        // configure the next question fetch
        await this.setupRepeatingQuestionFetching();
        // fetch them
        await this.fetchQuestions();
    }

    /**
     * Fetches the questions from the backend and stores them in the store.
     */
    public async fetchQuestions(): Promise<void> {
        // get current device config. There is nothing to do if the device has not been configured.
        const deviceStore = useDeviceStore();
        const deviceConfig = deviceStore.deviceConfig;
        if (!deviceConfig) return;

        // if the device shouldnt gather employee feedback, we dont need to run the fetch
        if (!deviceStore.deviceConfig?.gatherEmployeeFeedback) return;

        const deviceToken = deviceConfig.deviceId;

        const employeeFeedbackStore = useEmployeeFeedbackStore();

        const response = await api.employeeFeedbackService.getEmployeeFeedbackQuestions({
            structureNodeId: deviceConfig.structureNodeId,
            body: {
                deviceToken,
            },
        });
        employeeFeedbackStore.employeeFeedbackQuestions = response.activeQuestions;
    }

    public async saveEmployeeFeedback(
        employeeFeedbackQuestionId: string,
        recordedAssignmentId: string,
        answer: EmployeeFeedbackAnswerOptionDto | null,
        action: EmployeeFeedbackActiveAtAction,
    ): Promise<void> {
        // get current device config. There is nothing to do if the device has not been configured.
        const deviceStore = useDeviceStore();
        const deviceConfig = deviceStore.deviceConfig;
        if (!deviceConfig) return;

        // if the device shouldn't gather employee feedback, we dont need to save the answer
        if (!deviceStore.deviceConfig?.gatherEmployeeFeedback) return;

        const deviceToken = deviceConfig.deviceId;

        await api.employeeFeedbackService.saveEmployeeFeedbackAnswer({
            structureNodeId: deviceConfig.structureNodeId,
            body: {
                employeeFeedbackQuestionId,
                recordedAssignmentId,
                deviceToken,
                answer: answer ? answer : undefined,
                action: action,
            },
        });
    }
}
